<template>
    <div class="input-container">
        <div class="input-wrapper" :class="[{focus: isFocused}, {'input-error': errorMessage}]">
            <label for="cc-exp" :class="[{'float-label': isFocused || !isEmpty}]" :style="{background: labelBackground}">
                {{ label }}<span v-if="isRequired && !isFocused && isEmpty">*</span>
            </label>
            <input
                dusk="expiration-date"
                type="text"
                name="cc-exp"
                id="cc-exp"
                autocomplete="cc-exp"
                @input="validateInput"
                @blur="setBlur"
                @focus="setFocus"
                :placeholder="placeholder"
                maxlength="5"
                v-model="inputValue"
                inputmode="decimal"
                pattern="[0-9]*"
            />
            <svg v-if="errorMessage.length" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="error-icon" >
                <path fill="#C20202" d="M11.983,0a12.206,12.206,0,0,0-8.51,3.653A11.8,11.8,0,0,0,0,12.207,11.779,11.779,0,0,0,11.8,24h.214A12.111,12.111,0,0,0,24,11.791h0A11.766,11.766,0,0,0,11.983,0ZM10.5,16.542a1.476,1.476,0,0,1,1.449-1.53h.027a1.527,1.527,0,0,1,1.523,1.47,1.475,1.475,0,0,1-1.449,1.53h-.027A1.529,1.529,0,0,1,10.5,16.542ZM11,12.5v-6a1,1,0,0,1,2,0v6a1,1,0,1,1-2,0Z"/>
            </svg>
            <p v-if="errorMessage.length" class="error-message">{{ errorMessage }}</p>
        </div>
	</div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Emit, Model } from 'vue-property-decorator';

@Component
export default class ExpirationDate extends Vue {

    @Model('input', {type: String})         readonly value!: string;
    @Prop(String) readonly label!: string;
    @Prop({ type: String, default: "" }) readonly errorMessage!: string;
    @Prop({ type: Boolean, default: true }) readonly isRequired!: boolean;
    @Prop({ type: String, default: "#fff"}) readonly labelBackground!: string;

    isFocused: boolean = false;
    prevValue: string = '';

    @Emit('focus')
    setFocus() {
        this.isFocused = true;
    }

    @Emit('blur')
    setBlur() {
        this.isFocused = false;
    }

    validateInput(event: any) {
        if (event.target.value.length <= 2 || event.target.value.length > 3) {
            if (!/^\d+$/.test(event.target.value[event.target.value.length - 1])) {
                event.target.value = event.target.value.substring(0, event.target.value.length - 1);
            }
        }

        // check month validity (01 <= value <= 12)
        if (event.target.value.length === 2 && (event.target.value[0] === '1' && event.target.value[1] > 2) ||
            (event.target.value[0] === '0' && event.target.value[1] === '0')) {
            event.target.value = event.target.value.substring(0,event.target.value.length - 1);
        }

        // special situation: coming from a delete of just the month (keeping the year), autocomplete the month
        if (event.target.value.length === 4 && event.target.value[0] != 0 && event.target.value[0] != 1 && event.target.value[1] === '/') {
            event.target.value = '0' + event.target.value.slice(0,event.target.value.length);
        }

        // add '/' symbol after inserting month
        if (event.target.value.length === 2) {
            this.$emit('change', event.target.value + '/');
        } else if (event.target.value.length === 5 && event.target.value.length === 5 && event.target.value[0] === '1' && event.target.value[1] > 2) {
            // special situation: coming from a delete of just the year, autocomplete the month so the maximum one if user tries to enter a number > 12
            event.target.value = event.target.value.substring(0,1) + '2' + event.target.value.substring(2,5);
            this.$emit('change', event.target.value);
        } else {
            // normal situation
            this.$emit('change', event.target.value);
        }

        // remove '/' symbol after deleting year (last condition necessary to prevent special situations)
        if (this.prevValue.length === 4 && event.target.value.length === 3 && event.target.value[0] != '/') {
            this.$emit('change', event.target.value.slice(0,2));
        }

        // when first character is 2/3/4/5/6/7/8/9, emit value 02/03/04/05/06/07/08/09
        if (event.target.value.length === 1 && event.target.value[0] != 0 && event.target.value[0] != 1) {
            this.$emit('change', '0' + event.target.value + '/');
        }

        // special situation: when coming from a delete, insert the '/' symbol when the user starts typing the year
        if (event.target.value.length === 3 && this.prevValue.length === 3 && event.target.value[2] != '/') {
            this.$emit('change', event.target.value.slice(0,2) + '/' + event.target.value.slice(2,3));
        }
        this.prevValue = event.target.value;
    }

    get isEmpty() {
        if (this.value === '') {
            return true;
        }
        return false;
    }

    get placeholder() {
       if (this.isFocused) {
            return 'MM/YY';
        }
        return '';
    }

    get inputValue() {
        return this.value;
    }

    set inputValue(value: string) {
        this.$emit('change', value);
    }
}
</script>
<style lang="scss" scoped>
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.input-container {
    font-family: $body-font-family;
    color: $body-font-color;
    font-size: $body-font-size;
}

label {
    margin-bottom: 10px;
    position: absolute;
    top: 20px;
    left: 13px;
    transition: top .3s ease, font-size .3s ease;
    padding: 0 4px;
    width: fit-content;
    pointer-events: none;
    font-size: 1rem;
    line-height: 1rem;
    color: var(--battleship-gray);
    will-change: top, font-size;
}

input {
    height: 56px;
    border: 1px solid var(--medium-gray);
    border-radius: var(--global-radius);
    font-weight: 500;
    color: var(--dark-gray);
    padding: 16px 8px 0px 16px;
    box-shadow: none;
    margin-bottom: 22px; //keep space for error message


    &:hover {
        cursor: pointer;
        background-color: var(--almost-white);
    }
}

.focus {
    input {
        border: 2px solid var(--primary);
        box-shadow: none;
        padding-left: 15px; // prevent shifts of the content due to change of border width
    }

    & .float-label {
        color: $gray;
    }
}

.float-label {
    top: 11px;
    color: var(--battleship-gray);
    font-size: .75rem;
    transition: top .2s ease, font-size .2s ease;
}

.input-error {
    label.float-label {
        color: var(--alert);
    }
    input {
        border: 2px solid var(--alert);
        padding-left: 15px; // prevent shifts of the content due to change of border width
        margin-bottom: 0;
    }
}

.error-message {
    font-size: .7rem;
    margin-left: 12px;
    margin-top: 5px;
    color: var(--alert);
    margin-bottom: 0;
}

.input-wrapper {
    position: relative;
}

.error-icon {
    position: absolute;
    top: 16px;
    right: 10px;
    width: 24px;

    path {
        fill: var(--alert);
    }
}

</style>